import React, { useMemo, useState } from 'react';
import DatePicker from 'react-datepicker';
import 'react-datepicker/dist/react-datepicker.css';
import './index.css';
import { ReactDatePickerCustomHeaderProps } from 'react-datepicker/dist/calendar';
import { ReactComponent as Arrow } from 'assets/icons/blue-arrow-left.svg';
import { ReactComponent as Datepicker } from 'assets/icons/calendar.svg';
import moment from 'moment';
import { STANDARD_DATE } from 'utilities/dateHelper';
import { getWeekRange } from 'utilities/chartHelpers';
import {
  CalendarType,
  momentPeriodByCalendarType,
} from '../../app/calendar/const';
import { twMerge } from 'tailwind-merge';

const renderDayContents = (day: number) => {
  return <span className="flex items-center justify-center h-full">{day}</span>;
};

const renderCustomHeader = ({
  monthDate,
  decreaseMonth,
  increaseMonth,
}: ReactDatePickerCustomHeaderProps) => (
  <div>
    <button
      aria-label="Previous Month"
      className={
        'react-datepicker__navigation react-datepicker__navigation--previous'
      }
      onClick={decreaseMonth}
    >
      <Arrow className="h-[12px] fill-med-gray-3" />
    </button>
    <span className="react-datepicker__current-month">
      {monthDate.toLocaleString('en-US', {
        month: 'long',
        year: 'numeric',
      })}
    </span>
    <button
      aria-label="Next Month"
      className={
        'react-datepicker__navigation react-datepicker__navigation--next'
      }
      onClick={increaseMonth}
    >
      <Arrow className="rotate-180 h-[12px] fill-med-gray-3" />
    </button>
  </div>
);

interface ICustomRangeDatepickerProps {
  start?: string;
  end?: string;
  inline?: boolean;
  placeholderText?: string;
  minDate?: Date;
  maxDate?: Date;
  onDateChange: (from: string | undefined, to: string | undefined) => void;
}

const dateConverter = (date: Date | null) => {
  if (!date) {
    return undefined;
  }
  return date.toISOString();
};

const CustomRangeDatepicker: React.FC<ICustomRangeDatepickerProps> = ({
  start,
  end,
  minDate,
  maxDate,
  placeholderText = 'All dates',
  onDateChange,
}) => {
  const [startDate, setStartDate] = useState<Date | undefined>(() =>
    start ? new Date(start) : undefined,
  );
  const [endDate, setEndDate] = useState<Date | undefined>(() =>
    end ? new Date(end) : undefined,
  );
  const onChange = (dates: Array<unknown>) => {
    const [start, end] = dates;

    const startDate = new Date(start as Date);

    setStartDate(startDate);
    setEndDate(end as Date);

    onDateChange(dateConverter(startDate), dateConverter(end as Date));
  };

  return (
    <DatePicker
      showPopperArrow={false}
      selected={startDate}
      onChange={onChange}
      startDate={startDate}
      endDate={endDate}
      minDate={minDate}
      maxDate={maxDate}
      wrapperClassName="w-full"
      className="w-full"
      placeholderText={placeholderText}
      formatWeekDay={(nameOfDay) => nameOfDay.slice(0, 3)}
      renderDayContents={renderDayContents}
      renderCustomHeader={renderCustomHeader}
      icon={<Datepicker className="top-1/2 -translate-y-1/2 mr-2" />}
      popperPlacement="bottom"
      popperClassName="-mt-3"
      onKeyDown={(event) => event.preventDefault()}
      showIcon
      selectsRange
    />
  );
};

export const CustomWeeklyDatepicker: React.FC<ICustomRangeDatepickerProps> = ({
  start,
  end,
  minDate,
  maxDate,
  placeholderText = 'Select a week',
  inline = true,
  onDateChange,
}) => {
  const [startDate, setStartDate] = useState<Date | undefined>(() =>
    start ? new Date(start) : undefined,
  );
  const [endDate, setEndDate] = useState<Date | undefined>(() =>
    end ? new Date(end) : undefined,
  );

  const onChange = (date: Date | null) => {
    if (!date) {
      setStartDate(undefined);
      setEndDate(undefined);
      onDateChange(undefined, undefined);
      return;
    }

    const { start: weekStart, end: weekEnd } = getWeekRange(date, false);

    setStartDate(weekStart);
    setEndDate(weekEnd);

    onDateChange(dateConverter(weekStart), dateConverter(weekEnd));
  };

  const displayValue =
    startDate && endDate
      ? `${moment(startDate).format(STANDARD_DATE)} - ${moment(endDate).format(
          STANDARD_DATE,
        )}`
      : '';

  return (
    <DatePicker
      inline={inline}
      showPopperArrow={false}
      selected={startDate}
      onChange={onChange}
      startDate={startDate}
      endDate={endDate}
      minDate={minDate}
      maxDate={maxDate}
      value={displayValue}
      wrapperClassName="w-full"
      calendarClassName="!border-none shadow-sm w-full"
      className="w-full"
      placeholderText={placeholderText}
      formatWeekDay={(nameOfDay) => nameOfDay.slice(0, 3)}
      renderDayContents={renderDayContents}
      renderCustomHeader={renderCustomHeader}
      icon={<Datepicker className="top-1/2 -translate-y-1/2 mr-2" />}
      popperPlacement="bottom"
      popperClassName="-mt-3"
      onKeyDown={(event) => event.preventDefault()}
      showIcon
    />
  );
};

export const CustomDayDatepicker: React.FC<
  ICustomRangeDatepickerProps & {
    onDateChange: (date?: Date | null | string) => void;
    plugin: CalendarType;
  }
> = ({
  start,
  minDate,
  maxDate,
  placeholderText = 'Select a week',
  onDateChange,
  plugin,
}) => {
  const date = useMemo(() => (start ? new Date(start) : undefined), [start]);
  const onChange = (date: Date | null) => {
    if (!date) {
      onDateChange(undefined);
      return;
    }

    onDateChange(dateConverter(date));
  };

  const getDayClassName = (calendarDate: Date) => {
    if (!date || !plugin) return '';

    const period = momentPeriodByCalendarType[plugin];
    const startOfPeriod = moment(date).startOf(period);
    const endOfPeriod = moment(date).endOf(period);

    const isWithinPeriod = moment(calendarDate).isBetween(
      startOfPeriod,
      endOfPeriod,
      'day',
      '[]',
    );

    if (isWithinPeriod && !moment(calendarDate).isSame(date, 'day')) {
      return twMerge(
        'bg-hover-blue text-neutral-800',
        moment(calendarDate).isSame(startOfPeriod, 'day') && 'rounded-l-full',
        moment(calendarDate).isSame(endOfPeriod, 'day') && 'rounded-r-full',
      );
    }

    return '';
  };

  return (
    <DatePicker
      inline
      showPopperArrow={false}
      selected={date}
      onChange={onChange}
      minDate={minDate}
      maxDate={maxDate}
      wrapperClassName="w-full"
      calendarClassName="!border-none !bg-transparent w-full"
      className="w-full rounded-l-full"
      placeholderText={placeholderText}
      formatWeekDay={(nameOfDay) => nameOfDay.slice(0, 3)}
      renderDayContents={renderDayContents}
      renderCustomHeader={renderCustomHeader}
      dayClassName={getDayClassName}
      popperPlacement="bottom"
      popperClassName="-mt-3"
      onKeyDown={(event) => event.preventDefault()}
    />
  );
};

export default CustomRangeDatepicker;
